/**
 * Resuming a module's authorization request (PRD §4.1 steps 3–10).
 *
 * When a module sends a user here to sign in, the server parks the OAuth
 * parameters and hands the browser a single opaque handle. Every credential
 * screen carries that handle forward, and on success control returns to
 * `/authorize`, which finishes the flow and redirects into the module.
 *
 * Two things follow from the handle being opaque:
 *  - the portal never sees, stores, or has to validate a `redirect_uri`, so it
 *    cannot become the weak link in redirect protection (PRD §16)
 *  - resuming is a full page navigation to a server route, not a React
 *    transition — we are handing control back, not rendering another screen
 */
import { AUTH_REQUEST_PARAM, type AuthRequestSummary } from '@mindpeer/auth-shared';

export { AUTH_REQUEST_PARAM };

/**
 * Handles are server-generated (`authreq_` + nanoid). Validating the shape here
 * keeps a junk query parameter from reaching the API or a redirect.
 */
const HANDLE_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

/** Read the handle out of a location's query string, if there is a valid one. */
export function authRequestFrom(search: string): string | null {
  const raw = new URLSearchParams(search).get(AUTH_REQUEST_PARAM);
  return raw && HANDLE_PATTERN.test(raw) ? raw : null;
}

/** Append the handle to an internal portal path, preserving existing params. */
export function withAuthRequest(path: string, handle: string | null): string {
  if (!handle) return path;
  const [base, query] = path.split('?');
  const params = new URLSearchParams(query);
  params.set(AUTH_REQUEST_PARAM, handle);
  return `${base}?${params.toString()}`;
}

/**
 * Hand control back to the server to complete the authorization.
 *
 * `location.replace` rather than `assign`: the sign-in screen should not sit in
 * the history behind the module, where a back-navigation would re-run a spent
 * request and show a confusing "expired" error.
 */
export function resumeAuthorization(handle: string): void {
  const url = new URL('/authorize', window.location.origin);
  url.searchParams.set(AUTH_REQUEST_PARAM, handle);
  window.location.replace(url.toString());
}

/**
 * Fetch the application behind a handle, for "Continue to Meridian" copy.
 *
 * Returns null rather than throwing when the handle is expired or unknown: the
 * screen still works without the branding, and a dead handle is a normal
 * occurrence (a bookmarked sign-in URL), not an error worth showing.
 */
export async function fetchAuthRequest(handle: string): Promise<AuthRequestSummary | null> {
  try {
    const response = await fetch(`/api/auth-request/${encodeURIComponent(handle)}`, {
      credentials: 'include',
    });
    if (!response.ok) return null;
    return (await response.json()) as AuthRequestSummary;
  } catch {
    return null;
  }
}
